import { s3UtilityService } from "./S3Utilities";

/**
 * 🏛️ S3_ITERATIONS_MANAGER
 * Orchestrates multi-object operations for Rename, Label, and Delete.
 * Designed to provide iterative progress updates to the UI "Worker Halo".
 */
class S3IterationsManager {
    
    /**
     * 🏗️ ITERATIVE WORKER
     * Generic worker for multi-file tasks like BulkClean or Remove.
     */
    async runIterationWorker(bucketName, config, paths, actionType, onProgress) {
        const results = { success: [], failed: [] };
        const total = paths.length;

        for (let i = 0; i < total; i++) {
            const path = paths[i];
            
            // 📡 Update the UI Halo with the current file path
            if (onProgress) onProgress(i + 1, total, path);

            try {
                let response;
                // Standardizing to match Doorman.cs allowed intents
                if (actionType === "BulkClean") {
                    response = await s3UtilityService.executeBulkClean(bucketName, config, path);
                } else if (actionType === "Remove") {
                    response = await s3UtilityService.removeObject(bucketName, config, path);
                }
                results.success.push({ path, data: response });
            } catch (err) {
                results.failed.push({ path, error: err.message });
            }
        }
        return results;
    }

    /**
     * 🏷️ ITERATIVE LABELING
     * Applies specific tags to each selected file iteratively.
     */
    async labelIteration(bucketName, config, paths, tags, onProgress) {
        const results = { success: [], failed: [] };
        const total = paths.length;

        for (let i = 0; i < total; i++) {
            const path = paths[i];
            
            if (onProgress) onProgress(i + 1, total, path);

            try {
                // Relays to the s3UtilityService which hits the UpdateTags intent
                const response = await s3UtilityService.updateTags(bucketName, config, path, tags);
                results.success.push({ path, data: response });
            } catch (err) {
                results.failed.push({ path, error: err.message });
            }
        }
        return results;
    }

    /**
     * 📝 ITERATIVE RENAME
     * Prompts the user for a new name for each selected object.
     */
    async renameIteration(bucketName, config, paths, onProgress) {
        const results = { success: [], failed: [] };
        const total = paths.length;

        for (let i = 0; i < total; i++) {
            const path = paths[i];
            const oldName = path.split('/').pop();
            
            // 🎯 Neil System Authority: Explicit User Input for each file
            const newName = window.prompt(`RENAME AUTHORITY\nOriginal: ${oldName}\nEnter new filename:`, oldName);
            
            if (!newName || newName === oldName) {
                results.failed.push({ path, error: "Action cancelled or name unchanged." });
                continue;
            }

            if (onProgress) onProgress(i + 1, total, path);

            try {
                // Relays to Rename intent authorized in Doorman.cs
                const response = await s3UtilityService.renameObject(bucketName, config, path, newName);
                results.success.push({ path, data: response });
            } catch (err) {
                results.failed.push({ path, error: err.message });
            }
        }
        return results;
    }

    /**
     * 💾 SESSION CONTEXT SELECTOR
     * Injects the selected S3 paths into the global session object for other modules.
     */
    selectToSessionContext(paths) {
        try {
            const existing = JSON.parse(sessionStorage.getItem("SELECTED_S3_PATHS") || "[]");
            const combined = [...new Set([...existing, ...paths])]; 
            sessionStorage.setItem("SELECTED_S3_PATHS", JSON.stringify(combined));
            return { success: true, count: combined.length };
        } catch (err) {
            console.error("Context Injection Refused:", err);
            return { success: false };
        }
    }
}

export const IterationsManager = new S3IterationsManager();